import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { isEmpty } from "lodash";
import { useTheme } from "@mui/material/styles";
import { getStyles } from "./styles";
import {
  Box,
  Container,
  Grid,
  Link,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from "@mui/material";
import UserProvisioningData from "./UserSections/UserProvisioningData";
import UserTooling from "./UserSections/UserTooling";
import UserRosterData from "./UserSections/UserRosterData";
import { SiteDTO, LookupUserDTOV1, UserRole } from "@beable/roster-backend-client-v1";
import userSupportService from "../../services/UserSupportService";
import dataViewService from "../../services/DataViewService";
import UserLessonProgressData from "./UserSections/UserLessonProgressData";

interface UserCourseTableProps {
  users: LookupUserDTOV1[];
  site: SiteDTO;
  orgId: string | undefined;
}

export default function UserSearchResults({
  users,
  site,
  orgId,
}: UserCourseTableProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [selectedUser, setSelectedUser] = useState<
    LookupUserDTOV1 | undefined
  >();
  const [userRole, setUserRole] = useState<string | null>(null);
  const [activeTab, setActiveTab] = React.useState("1");

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  const changeUserApplicationLanguage = (languageCode: string) => {
    if (selectedUser) {
      users.find((u) => u.userId === selectedUser.userId)!.applicationLanguage =
        languageCode;

      const modifiedUser = {
        ...selectedUser,
        applicationLanguage: languageCode,
      };
      setSelectedUser(modifiedUser);
    }
  };

  const setUserInfo = async (user: LookupUserDTOV1, site: SiteDTO) => {
    setSelectedUser(user);

    if (user.userAccountId) {
        const roles = await userSupportService.findRolesForUserAccount(user.id, site.id);
        setUserRole(roles ? roles[0] : null);
    } else {
        const role = await dataViewService.getUserRole(user.userId);
        setUserRole(role);
    }
  }

  const handleBackToResults = () => {
    setSelectedUser(undefined);
    setActiveTab("1");
  };

  const displayRow = (user: LookupUserDTOV1) => {
    if (!selectedUser || selectedUser.id === user.id) {
      return (
        <TableRow
          id={user.id}
          key={user.id}
          onClick={() => setUserInfo(user, site)}
          sx={[
            ...user.id === selectedUser?.id ? [styles.userSearchSelectedRow] : [],
            {
              display: "inlineTable",
              width: "100%",
            }
          ]}
        >
          <TableCell
            component="th"
            scope="row"
            id={"tbl-cell-username-" + user.username}
          >
            {user.username}
          </TableCell>
          <TableCell align="left" id={"tbl-cell-firstName-" + user.username}>
            {user.firstName}
          </TableCell>
          <TableCell align="left" id={"tbl-cell-lastName-" + user.username}>
            {user.lastName}
          </TableCell>
          <TableCell align="left" id={"tbl-cell-userLanguage-" + user.username}>
            {user.applicationLanguage ? (user.applicationLanguage === "en" ? "English" : "Spanish") : ("N/A")}
          </TableCell>
        </TableRow>
      );
    } else {
      return;
    }
  };

  useEffect(() => {
    setSelectedUser(undefined);
    setActiveTab("1");
  }, [users]);

  if (isEmpty(users)) {
    return (
      <Container sx={styles.schedule_root}>
        <Typography>No users found</Typography>
      </Container>
    );
  }

  return (
    <Container sx={styles.schedule_root}>
      <TableContainer component={Paper} sx={styles.dfb}>
        <Table sx={styles.userSearchResultsTable}>
          {!selectedUser ? (
            <TableHead>
              <TableRow sx={{ display: "inlineTable", width: "100%" }}>
                <TableCell>Username</TableCell>
                <TableCell align="left">First&nbsp;Name</TableCell>
                <TableCell align="left">Last&nbsp;Name</TableCell>
                <TableCell align="left">Application&nbsp;Language</TableCell>
              </TableRow>
            </TableHead>
          ) : (
            <TableHead>
              <TableRow>
                <TableCell colSpan={4}>
                  <Link href="#" onClick={handleBackToResults} underline="hover">
                    <Grid container>
                      <Grid>
                        <ArrowBackIcon />
                      </Grid>
                      <Grid>Back to search results</Grid>
                    </Grid>
                  </Link>
                </TableCell>
              </TableRow>
            </TableHead>
          )}
          <TableBody>{users.map((user) => displayRow(user))}</TableBody>
        </Table>
      </TableContainer>
      {users && selectedUser && (
        <Box sx={styles.schedule_details}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={styles.tab_root}
            textColor="inherit"
            indicatorColor='secondary'
          >
            <Tab label="Tools" value="1" />
            <Tab label="Literacy Provisioning" value="2" />
            <Tab label="Roster View" value="3" />
            {userRole === UserRole.student && (<Tab label="Lesson Progress" value="4" />)}
          </Tabs>
          {activeTab === "1" && (
            <UserTooling
              user={selectedUser}
              userRole={userRole}
              site={site}
              onLanguageChange={changeUserApplicationLanguage}
            />
          )}
          {activeTab === "2" && (
            <UserProvisioningData
              user={selectedUser}
              userRole={userRole}
              siteId={site.id}
              orgId={orgId}
            />
          )}
          {activeTab === "3" && <UserRosterData user={selectedUser} />}
          { activeTab === "4" && (
            <UserLessonProgressData
            user={selectedUser}
            site={site}
            />
          )}
        </Box>
      )}
    </Container>
  );
}
